import React, { useEffect, useRef, useState } from "react";
import pdfjsLib from "@/lib/pdfWorker";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function AnnotatedScript({ scriptId, annotations = [] }) {
  const [pages, setPages] = useState([]); // {dataUrl, w, h}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const containerRef = useRef();

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/answer-scripts/${scriptId}/pdf`, { responseType: "arraybuffer" });
        let pdf;
        try {
          pdf = await pdfjsLib.getDocument({ data: res.data }).promise;
        } catch (renderError) {
          const wrappedError = new Error("PDF_RENDER_FAILED");
          wrappedError.cause = renderError;
          throw wrappedError;
        }
        const width = Math.min(containerRef.current?.clientWidth || 520, 700);
        const out = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = width / base.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext("2d");
          await page.render({ canvasContext: ctx, viewport }).promise;
          out.push({ dataUrl: canvas.toDataURL("image/jpeg", 0.7), w: viewport.width, h: viewport.height });
        }
        if (!cancelled) setPages(out);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 404) {
            setError("The scanned answer PDF is no longer available on this server.");
          } else if (e.message === "PDF_RENDER_FAILED") {
            setError("The scanned answer PDF was downloaded but could not be rendered. Please refresh and try again.");
          } else {
            setError("The scanned answer PDF could not be loaded. Please try again.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    render();
    return () => { cancelled = true; };
  }, [scriptId, retry]);

  // global numbering for pins
  let counter = 0;
  const numbered = annotations.map((a) => ({ ...a, n: ++counter }));
  if (error) {
    return (
      <div className="bg-background border border-border rounded-lg p-6 text-sm" role="alert" data-testid="scanned-pdf-error">
        <p className="text-destructive">{error}</p>
        <button type="button" className="mt-3 text-primary underline" onClick={() => setRetry((value) => value + 1)}>Try again</button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-background border border-border rounded-lg p-3" data-testid="annotated-script">
      {loading && (
        <div className="h-64 flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Rendering your script…</div>
      )}
      <div className="space-y-4 max-h-[74vh] overflow-y-auto">
        {pages.map((pg, idx) => {
          const pins = numbered.filter((a) => (a.page || 1) === idx + 1);
          return (
            <div key={idx} className="relative mx-auto" style={{ width: pg.w }}>
              <img src={pg.dataUrl} alt={`page ${idx + 1}`} className="w-full rounded border border-border" />
              {pins.map((a) => {
                const x = a.x || 0, y = a.y || 0;
                const hasBox = typeof a.w === "number" && typeof a.h === "number" && a.w > 0 && a.h > 0;
                if (hasBox) {
                  const bx = Math.max(0, Math.min(x, 0.99));
                  const by = Math.max(0, Math.min(y, 0.99));
                  const bw = Math.min(a.w, 1 - bx);
                  const bh = Math.min(a.h, 1 - by);
                  return (
                    <div
                      key={a.n}
                      data-testid={`annotation-box-${a.n}`}
                      title={a.note}
                      className="absolute group border-2 border-destructive rounded-[3px] bg-destructive/10"
                      style={{ left: `${bx * 100}%`, top: `${by * 100}%`, width: `${bw * 100}%`, height: `${bh * 100}%` }}
                    >
                      <span className="absolute -top-3 -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-destructive text-white text-xs font-bold ring-2 ring-white shadow-md cursor-help">{a.n}</span>
                      <span className="absolute left-0 top-full mt-1 hidden group-hover:block z-10 bg-destructive text-white text-xs rounded px-2 py-1 w-48 shadow-lg">{a.note}</span>
                    </div>
                  );
                }
                // Backward-compat: point annotations (no w/h) fall back to an offset numbered pin
                const offX = x < 0.12 ? "40%" : "-140%";
                const offY = y < 0.1 ? "40%" : "-140%";
                return (
                <div
                  key={a.n}
                  data-testid={`annotation-pin-${a.n}`}
                  title={a.note}
                  className="absolute group"
                  style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
                >
                  {/* precise marker at the exact mistake location (small, does not hide text) */}
                  <span className="absolute -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-destructive/30 ring-2 ring-destructive" />
                  {/* numbered pin offset into the margin so it never covers the handwriting */}
                  <span className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-destructive text-white text-xs font-bold ring-2 ring-white shadow-md cursor-help" style={{ transform: `translate(${offX}, ${offY})` }}>{a.n}</span>
                  <span className="absolute left-7 top-0 hidden group-hover:block z-10 bg-destructive text-white text-xs rounded px-2 py-1 w-48 shadow-lg">{a.note}</span>
                </div>
              );})}
            </div>
          );
        })}
      </div>
      {numbered.length > 0 && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="overline text-muted-foreground mb-2">Marked mistakes</p>
          <ol className="space-y-1.5">
            {numbered.map((a) => (
              <li key={a.n} className="flex items-start gap-2 text-sm">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-destructive text-white text-[10px] font-bold shrink-0 mt-0.5">{a.n}</span>
                <span className="text-destructive">{a.note}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
